'use client'

import { ReferenceDocumentation } from '@criteria-labs/docs-kit/react'
import { ErrorPane } from '@criteria-labs/react'
import { OpenAPI as OpenAPIv3_0 } from '@criteria/openapi/v3.0'
import { OpenAPI as OpenAPIv3_1 } from '@criteria/openapi/v3.1'
import { useSearchParams } from 'next/navigation'
import { useEffect, useMemo, useState } from 'react'
import { APIExplorerPanelBody, APIExplorerPanelHeader } from '../../api-explorer/APIExplorer'
import { APIExplorerProvider } from '../../api-explorer/APIExplorerProvider'
import { APIExplorerSettings } from '../../api-explorer/APIExplorerSettings'
import { ApplicationLayout } from '../../components/application'
import { ConsoleLayout, ConsolePanel } from '../../console/ConsoleLayout'
import { ActiveDocumentView } from '../../document-view/ActiveDocumentView'
import { useDocuments } from '../../documents/DocumentStore'
import { OpenAPIDocument, OpenAPIDocumentList, useOpenAPIDocumentListOpenAPI } from '../../documents/OpenAPIDocument'
import { PlaygroundHeader } from '../../header/PlaygroundHeader'
import { SourceRange } from '../../issues/Issue'
import { IssueNavigatorPanelBody, IssueNavigatorPanelHeader } from '../../issues/IssueNavigator'
import { useBaseURL } from '../../lib/useBaseURL'
import { Playground, PlaygroundSnapshot } from '../../playground/Playground'
import { ProjectLayout, useProjectSelection } from '../../project'
import { ImportOpenAPIDialog } from './ImportOpenAPIDialog'

export default function PlaygroundPageClientContent({ projectID }: { projectID: string | null }) {
  const baseURL = useBaseURL()

  const hrefForSnapshot = (snapshot: PlaygroundSnapshot) =>
    `${baseURL}/${snapshot.id}${snapshot.view === 'docs' ? '?view=docs' : ''}`

  const [consoleTab, setConsoleTab] = useState<'api_explorer' | 'issues'>('api_explorer')
  const { select } = useProjectSelection()

  const [textSelection, setTextSelection] = useState<SourceRange | null>(null)

  const [showingSettings, setShowingSettings] = useState(false)

  const searchParams = useSearchParams()
  const initialViewMode = searchParams.get('view') === 'docs' ? 'docs' : 'editor'
  const [viewMode, setViewMode] = useState<'editor' | 'docs'>(initialViewMode)

  const [importOpenAPIOpen, setImportOpenAPIOpen] = useState(false)
  useEffect(() => {
    if (window.location.hash === '#import') {
      setImportOpenAPIOpen(true)
    }
  }, [])

  const [documentMode, setDocumentMode] = useState<'code' | 'inspector'>('code')

  return (
    <Playground
      defaultSnapshotID={projectID}
      defaultView={viewMode}
      hrefForSnapshot={hrefForSnapshot}
      onCreateSnapshot={(snapshot) => {
        window.history.pushState({}, '', hrefForSnapshot(snapshot))
      }}
      fallback={(error) => (
        <ApplicationLayout header={<PlaygroundHeader items={[]} viewMode={viewMode} setViewMode={setViewMode} />}>
          <main className="h-full">
            {/* TODO: CTA to clear and load new */}
            <ErrorPane error={error} />
          </main>
        </ApplicationLayout>
      )}
    >
      <ApplicationLayout header={<PlaygroundHeader items={[]} viewMode={viewMode} setViewMode={setViewMode} />}>
        <main className="h-full">
          <APIExplorerProvider>
            <ConsoleLayout
              console={({ setOpen }) => (
                <ConsolePanel
                  header={
                    <div className="flex gap-2">
                      <APIExplorerPanelHeader
                        selected={!showingSettings && consoleTab === 'api_explorer'}
                        onClick={() => {
                          setShowingSettings(false)
                          setConsoleTab('api_explorer')
                        }}
                      />
                      <IssueNavigatorPanelHeader
                        selected={!showingSettings && consoleTab === 'issues'}
                        onClick={() => {
                          setShowingSettings(false)
                          setConsoleTab('issues')
                        }}
                      />
                    </div>
                  }
                  onClose={() => setOpen(false)}
                  showingSettings={showingSettings}
                  setShowingSettings={setShowingSettings}
                >
                  {!showingSettings && consoleTab === 'api_explorer' && <APIExplorerPanelBody />}
                  {!showingSettings && consoleTab === 'issues' && (
                    <IssueNavigatorPanelBody
                      onGoTo={(destination) => {
                        select(destination.filePath as `/${string}`, false)
                        setTextSelection(destination.textSelection)
                      }}
                    />
                  )}
                  {showingSettings && <APIExplorerSettings />}
                </ConsolePanel>
              )}
            >
              {viewMode === 'editor' && (
                <ProjectLayout documentMode={documentMode} setDocumentMode={setDocumentMode}>
                  <ActiveDocumentView textSelection={textSelection ?? undefined} mode={documentMode} />
                </ProjectLayout>
              )}
              {viewMode === 'docs' && <OpenAPIDocumentation baseURL={baseURL} />}
            </ConsoleLayout>
          </APIExplorerProvider>
        </main>
      </ApplicationLayout>
      <ImportOpenAPIDialog
        open={importOpenAPIOpen}
        onClose={() => {
          setImportOpenAPIOpen(false)
          window.location.hash = ''
        }}
      />
    </Playground>
  )
}

function OpenAPIDocumentation({ baseURL }: { baseURL: string }) {
  const params = useSearchParams()
  const slug = params.get('slug')

  const documents = useDocuments()
  const openAPIDocumentList = useMemo(() => {
    const openAPIDocuments = documents.filter((document) => document instanceof OpenAPIDocument) as OpenAPIDocument[]
    return new OpenAPIDocumentList(openAPIDocuments)
  }, [documents])

  const openAPIs = useOpenAPIDocumentListOpenAPI(openAPIDocumentList)
  const validOpenAPIs = openAPIs.filter(Boolean) as (OpenAPIv3_0<never> | OpenAPIv3_1<never>)[]

  return <ReferenceDocumentation openAPIs={validOpenAPIs} />
}
